// RF chat wire schemas (#1066). Mirror of the China-side chat
// contracts (`actors-chat-audit.ts` + `chat-read-state.ts`) bound to
// the RU-NNNNN id grammar so RF and China responses never share a
// wire-level message or unread-summary shape.

import { z } from 'zod';
import { UserActorRefSchema } from './requests/actors-chat-audit.js';

const RussiaRequestIdLiteralSchema = z.string().regex(/^RU-\d{5}$/, 'must match RU-NNNNN');

/**
 * @aiPurpose wire projection of a single RF chat message. Mirrors
 *   `ChatMessageSchema` (China) field-for-field — same `id/by/at/text/
 *   readByCounterparty` shape — so the frontend can lean on the
 *   existing chat renderer. `readByCounterparty` is server-computed
 *   per message against the parent RF row's CURRENT
 *   `{managerId, logistId}` pair, never from a sticky per-message
 *   flag; reassignment recalculates it.
 * @aiInvariant `.strict()` rejects extra keys so a future widening of
 *   the wire shape lands intentionally with a coordinated frontend +
 *   backend change.
 * @aiPii surfaces actor name and free-text body; redact in logs.
 *   `readByCounterparty` reveals that the counterparty has viewed the
 *   chat tail, but the exact `lastReadAt` timestamp never lands on the
 *   wire.
 * @aiSecurity backend MUST populate the boolean for live chat
 *   endpoints (`GET /russia-requests/:id/chat`, `POST /russia-requests/:id/chat`).
 *   `false` when not applicable (own message, former-party
 *   message, admin/observer-authored message, no counterparty, or
 *   counterparty not yet read).
 */
export const RussiaRequestChatMessageSchema = z
  .object({
    id: z.string().uuid(),
    by: UserActorRefSchema,
    at: z.string().datetime(),
    text: z.string().min(1),
    readByCounterparty: z.boolean().optional(),
  })
  .strict();
export type RussiaRequestChatMessage = z.infer<typeof RussiaRequestChatMessageSchema>;

/**
 * @aiPurpose response envelope for
 *   `GET /russia-requests/:id/chat?cursor=...&limit=...`. Cursor
 *   pagination mirrors the China chat contract; `nextCursor` is an
 *   opaque server-issued string the client passes back verbatim.
 */
export const RussiaRequestChatListResponseSchema = z
  .object({
    items: z.array(RussiaRequestChatMessageSchema),
    nextCursor: z.string().nullable(),
  })
  .strict();
export type RussiaRequestChatListResponse = z.infer<typeof RussiaRequestChatListResponseSchema>;

/**
 * @aiPurpose body for `POST /russia-requests/:id/chat`. Server uses
 *   the JWT actor as the message author and the URL `:id` as the
 *   parent RF request; the body carries only the message text.
 */
export const RussiaRequestChatPostBodySchema = z
  .object({
    body: z.string().min(1).max(10_000),
  })
  .strict();
export type RussiaRequestChatPostBody = z.infer<typeof RussiaRequestChatPostBodySchema>;

// ---- RF chat read-state (#1066, mirror of #570) ---------------------

/**
 * @aiPurpose hard cap on the number of RF request IDs the unread-
 *   summary endpoint accepts per call. Same bound as the China side
 *   (200) so a logist_ru loading the full RF list can fan one
 *   aggregate query instead of per-row lookups.
 */
export const RUSSIA_REQUEST_CHAT_UNREAD_SUMMARY_MAX_IDS = 200;

const SingleRussiaRequestIdOrArraySchema = z.union([
  z.array(RussiaRequestIdLiteralSchema),
  RussiaRequestIdLiteralSchema.transform((s) => [s]),
  z
    .string()
    .transform((s) =>
      s
        .split(',')
        .map((p) => p.trim())
        .filter(Boolean),
    )
    .pipe(z.array(RussiaRequestIdLiteralSchema).min(1)),
]);

/**
 * @aiPurpose query string for
 *   `GET /russia-requests/chat-unread-summary?russiaRequestId=RU-00001&russiaRequestId=RU-00002`.
 *   Flat literal (not nested under `:id`) so it cannot collide with
 *   the `:id` capture on the controller.
 * @aiInvariant `russiaRequestId` accepts a single value, an array,
 *   OR a comma-separated string for transport-form parity. Output is
 *   normalized to a non-empty array of `RU-NNNNN` strings ≤
 *   `RUSSIA_REQUEST_CHAT_UNREAD_SUMMARY_MAX_IDS`.
 */
export const RussiaRequestChatUnreadSummaryQuerySchema = z
  .object({
    russiaRequestId: SingleRussiaRequestIdOrArraySchema.pipe(
      z.array(RussiaRequestIdLiteralSchema).min(1).max(RUSSIA_REQUEST_CHAT_UNREAD_SUMMARY_MAX_IDS),
    ),
  })
  .strict();
export type RussiaRequestChatUnreadSummaryQuery = z.infer<
  typeof RussiaRequestChatUnreadSummaryQuerySchema
>;

/**
 * @aiPurpose per-RF-request unread summary row.
 * @aiInvariant `unreadCount` excludes the current user's own
 *   messages and counts only messages strictly newer than
 *   `COALESCE(read_state.lastReadAt, rollout.baselineAt)`.
 * @aiInvariant `isMarkedUnread` is the user's personal flag; it
 *   does NOT move `lastReadAt`.
 * @aiPii does NOT include actor names, RF details, chat text, or
 *   exact read timestamps.
 */
export const RussiaRequestChatUnreadSummaryItemSchema = z
  .object({
    russiaRequestId: RussiaRequestIdLiteralSchema,
    unreadCount: z.number().int().nonnegative(),
    isMarkedUnread: z.boolean(),
  })
  .strict();
export type RussiaRequestChatUnreadSummaryItem = z.infer<
  typeof RussiaRequestChatUnreadSummaryItemSchema
>;

/**
 * @aiPurpose response envelope for the RF unread-summary endpoint.
 *   Flat array keyed by `russiaRequestId`; invisible RF ids are
 *   omitted (existence is never disclosed) so the response surface
 *   is a subset of the requested ids.
 */
export const RussiaRequestChatUnreadSummaryResponseSchema = z.array(
  RussiaRequestChatUnreadSummaryItemSchema,
);
export type RussiaRequestChatUnreadSummaryResponse = z.infer<
  typeof RussiaRequestChatUnreadSummaryResponseSchema
>;

/**
 * @aiPurpose body for `POST /russia-requests/:id/chat/read`.
 *   Carries the chronologically newest visible message id; the
 *   server derives the timestamp from the persisted row and advances
 *   `lastReadAt` monotonically via DB-level `GREATEST`.
 * @aiInvariant `messageId` is a UUID —
 *   `RussiaRequestChatMessage.id` is `@default(uuid())` in Prisma.
 */
export const RussiaRequestChatReadBodySchema = z
  .object({
    messageId: z.string().uuid(),
  })
  .strict();
export type RussiaRequestChatReadBody = z.infer<typeof RussiaRequestChatReadBodySchema>;
